import { useMemo } from 'react'

import {
  ApplicantStage,
  DisqualifiedReason,
  HiringStage,
  NotInterestedReason,
} from 'src/graphql/types/applicants'
import { formatSnakeValue } from 'src/lib/formatters'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'

import { FilterOption } from '../types'

interface UseApplicantStagesFilterProps {
  selectedApplicantStages: string[]
  onApplicantStagesChange: (applicantStages: string[]) => void
}

export const useApplicantStagesFilter = ({
  selectedApplicantStages,
  onApplicantStagesChange,
}: UseApplicantStagesFilterProps) => {
  const baseOptions = useMemo<FilterOption[]>(() => {
    return Object.values(ApplicantStage).map((applicantStage) => {
      if (applicantStage === ApplicantStage.PROSPECT) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(HiringStage).map((hiringStage) => ({
            value: hiringStage,
            label: formatSnakeValue(hiringStage),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.HIRED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: [
            {
              value: 'ALL',
              label: 'All',
              selected: false,
            },
          ],
        }
      }
      if (applicantStage === ApplicantStage.DISQUALIFIED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(DisqualifiedReason).map((reason) => ({
            value: reason,
            label: formatSnakeValue(reason),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.NOT_INTERESTED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: Object.values(NotInterestedReason).map((reason) => ({
            value: reason,
            label: formatSnakeValue(reason),
            selected: false,
          })),
        }
      }
      if (applicantStage === ApplicantStage.TERMINATED) {
        return {
          value: applicantStage,
          label: formatSnakeValue(applicantStage),
          selected: false,
          options: TERMINATED_REASON_OPTIONS.map((option) => ({
            ...option,
            selected: false,
          })),
        }
      }
      return {
        value: applicantStage,
        label: formatSnakeValue(applicantStage),
        selected: false,
        options: [],
      }
    })
  }, [])

  // Update options to reflect selected state
  const options = useMemo<FilterOption[]>(() => {
    return baseOptions.map((option) => {
      const isMainSelected = selectedApplicantStages.some((selected) => {
        if (selected.includes(':')) {
          const [stage] = selected.split(':')
          return stage === option.value
        }
        return selected === option.value
      })

      const updatedSubOptions = option.options?.map((subOption) => {
        const isSubSelected = selectedApplicantStages.some((selected) => {
          if (selected.includes(':')) {
            const [stage, reason] = selected.split(':')
            return stage === option.value && reason === subOption.value
          }
          return false
        })

        return {
          ...subOption,
          selected: isSubSelected,
        }
      })

      // Check if any substages are selected to determine if parent should be selected
      const hasSelectedSubOptions =
        updatedSubOptions?.some((subOption) => subOption.selected) || false

      return {
        ...option,
        selected: isMainSelected || hasSelectedSubOptions,
        options: updatedSubOptions,
      }
    })
  }, [baseOptions, selectedApplicantStages])

  return {
    options,
    selected: selectedApplicantStages,
    onChange: onApplicantStagesChange,
    isLoaded: true,
  }
}
